import * as z from "zod";
import { type DisputeAlertType } from "./dispute-alert-definition";
import { type DisputeStatus } from "./dispute-definition";

/**
 * Repo row shapes for the merchant chargeback-analytics sections. Amounts are
 * as each table stores them: `dispute.amount` major units, OTD minor units.
 */

/**
 * One row per (BIN prefix, bank name, currency). `bankName` doubles as the
 * payment-method label for non-card disputes, so the handler buckets both.
 */
export interface PaymentMethodBreakdownRow {
  binPrefix?: string | null;
  bankName?: string | null;
  currency: string;
  count: number;
  totalAmount: number;
}

/** A chargeback's order and dates, for the by-product and time-to-event joins. */
export interface DisputeOrderRow {
  sourceOrderId: string;
  status: DisputeStatus;
  amount: number;
  currency: string;
  initiatedAt: Date;
}

/** Raw network reason codes per alert program; mapped to reasons in the handler. */
export interface AlertReasonCodeRow {
  type: DisputeAlertType;
  reasonCode?: string | null;
  providerAlertType?: string | null;
  count: number;
}

/** Whole days from the disputed transaction to the alert; may be negative. */
export interface AlertTimeToEventRow {
  days: number;
  count: number;
}

/**
 * Monthly transaction volume per card brand. `totalCount` sits beside the
 * settled denominator so a caller can see how much the settled filter dropped.
 */
export interface MonthlyTransactionCountRow {
  month: Date;
  cardBrand?: string | null;
  settledCount: number;
  totalCount: number;
}

/** One slice of the prevention composition; chargebacks are the missed slice. */
export const coverageSliceSchema = z.object({
  key: z.string(),
  count: z.coerce.number(),
  share: z.coerce.number().nullable(),
});

/** Paired alert/dispute counts for one age bucket of the time-to-dispute chart. */
export const timeToEventBucketSchema = z.object({
  bucket: z.string(),
  label: z.string(),
  alertCount: z.coerce.number(),
  disputeCount: z.coerce.number(),
});

/** Per-month rates; nulls where transaction volume is unavailable. */
export const monthlyDisputeRateSchema = z.object({
  month: z.string(),
  chargebackCount: z.coerce.number(),
  alertCount: z.coerce.number(),
  transactionCount: z.coerce.number(),
  ratio: z.coerce.number().nullable(),
  counterfactualRatio: z.coerce.number().nullable(),
});

export const brandDisputeRateSchema = z.object({
  cardBrand: z.string().nullable(),
  label: z.string(),
  chargebackCount: z.coerce.number(),
  transactionCount: z.coerce.number(),
  ratio: z.coerce.number().nullable(),
});

/**
 * Coverage + ratios: one payload, since both read the same alert, chargeback
 * and transaction-volume aggregates.
 */
export const coverageStatsSchema = z.object({
  rdrCount: z.coerce.number(),
  cdrnCount: z.coerce.number(),
  ethocaCount: z.coerce.number(),
  chargebackCount: z.coerce.number(),
  slices: z.array(coverageSliceSchema),
  preventedCount: z.coerce.number(),
  preventedShare: z.coerce.number().nullable(),
  catchRate: z.coerce.number().nullable(),
  /** Settled card volume; the denominator behind every rate here. */
  cardTransactionCount: z.coerce.number(),
  /** Live rows before the settled filter, so a shrunken denominator is visible. */
  totalTransactionCount: z.coerce.number(),
  transactionDataAvailable: z.boolean(),
  withAlertsRatio: z.coerce.number().nullable(),
  withoutAlertsRatio: z.coerce.number().nullable(),
  thresholdRatio: z.coerce.number(),
  timeToEvent: z.array(timeToEventBucketSchema),
  timeToEventUnknownAlertCount: z.coerce.number(),
  timeToEventUnknownDisputeCount: z.coerce.number(),
  monthly: z.array(monthlyDisputeRateSchema),
  byBrand: z.array(brandDisputeRateSchema),
  alertsIncluded: z.boolean(),
  truncated: z.boolean(),
  errors: z.array(z.string()).nullish(),
});

export type CoverageStats = z.infer<typeof coverageStatsSchema>;

export const paymentMethodStatSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(["card", "payment_method", "unknown"]),
  cardBrand: z.string().nullable(),
  /** False for wallets and BNPL, which no card-network alert can prevent. */
  protectable: z.boolean(),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
});

export const paymentMethodStatsSchema = z.object({
  methods: z.array(paymentMethodStatSchema),
  totalCount: z.coerce.number(),
  currency: z.string(),
  errors: z.array(z.string()).nullish(),
});

export type PaymentMethodStats = z.infer<typeof paymentMethodStatsSchema>;

export const unifiedReasonStatSchema = z.object({
  reason: z.string(),
  label: z.string(),
  disputeCount: z.coerce.number(),
  alertCount: z.coerce.number(),
});

export const unifiedReasonStatsSchema = z.object({
  reasons: z.array(unifiedReasonStatSchema),
  /** Alerts whose network code we do not model; shown, never redistributed. */
  unclassifiedAlertCount: z.coerce.number(),
  alertsIncluded: z.boolean(),
  errors: z.array(z.string()).nullish(),
});

export type UnifiedReasonStats = z.infer<typeof unifiedReasonStatsSchema>;

export const disputeProductStatSchema = z.object({
  productKey: z.string(),
  title: z.string(),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
});

export const disputeProductStatsSchema = z.object({
  products: z.array(disputeProductStatSchema),
  matchedCount: z.coerce.number(),
  /** Chargebacks whose order is not in Redo; reported so shares stay honest. */
  unmatchedCount: z.coerce.number(),
  totalCount: z.coerce.number(),
  currency: z.string(),
  truncated: z.boolean(),
  errors: z.array(z.string()).nullish(),
});

export type DisputeProductStats = z.infer<typeof disputeProductStatsSchema>;
